import React, { useState, useEffect, useRef, useCallback } from "react";
import pkgJson from "../../package.json";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import NavBar from "../components/layout/NavBar";
import { HeaderBar } from "../components/layout/TopBar";
import { Hero } from "../components/adminSurface/AdminSurface";
import { UpdateArt } from "../components/adminSurface/heroArt";
import {
  getUpdateStatus,
  forceCheckUpdate,
  applyUpdate,
  getUpdateProgress,
  getLastUpdateJob,
  dismissUpdateJob,
  openUpdateStream,
  getComponentVersions,
  getFleetVersions,
  updateFleet,
  getFleetStatus,
} from "../api/updateApi";

// ── Small icon set (inline SVG, consistent with AdminHub) ────────────────────
const Icon = ({ name, size = 20, className = "" }) => {
  const p = {
    width: size, height: size, viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor",
    strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", className,
  };
  const paths = {
    refresh: <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />,
    download: <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>,
    check:    <polyline points="20 6 9 17 4 12" />,
    x:        <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
    info:     <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
    terminal: <><polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" /></>,
    clock:    <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
    minus:    <line x1="5" y1="12" x2="19" y2="12" />,
  };
  return <svg {...p}>{paths[name] || null}</svg>;
};

// ── Phase label map ───────────────────────────────────────────────────────────
const PHASE_KEYS = {
  starting:          "update.phase.starting",
  installing_package:"update.phase.installingPackage",
  stopping_services: "update.phase.stoppingServices",
  copying_binaries:  "update.phase.copyingBinaries",
  starting_services: "update.phase.startingServices",
  complete:          "update.phase.complete",
  failed:            "update.phase.failed",
};

// ── Confirmation modal ────────────────────────────────────────────────────────
function ConfirmModal({ version, onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <div className="upd-overlay">
      <div className="upd-modal">
        <h3 className="upd-modal__title">{t("update.confirmTitle")}</h3>
        <p className="upd-modal__body">
          {t("update.confirmMessage", { version })}
        </p>
        <div className="upd-modal__actions">
          <button className="adm-btn" style={{ background:"#ef4444",color:"#fff",border:"none" }} onClick={onConfirm}>
            {t("update.confirmYes")}
          </button>
          <button className="adm-btn adm-btn--outline" onClick={onCancel}>
            {t("update.confirmNo")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function UpdatePage() {
  const { t }       = useTranslation();
  const { token }   = useAuth();

  // Version state
  const [versionInfo, setVersionInfo] = useState(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versionError, setVersionError]     = useState(null);

  // Component versions state
  const [components, setComponents]       = useState(null);
  // The other servers of this installation. Null until read; an installation of
  // one machine simply has none, and the section stays hidden.
  const [fleet, setFleet]                 = useState(null);
  const [fleetBusy, setFleetBusy]         = useState(false);
  const [fleetError, setFleetError]       = useState("");
  const [fleetNote, setFleetNote]         = useState("");
  const [compLoading, setCompLoading]     = useState(false);

  // Job / progress state
  const [jobId, setJobId]             = useState(null);
  const [jobStatus, setJobStatus]     = useState(null); // job object
  const [logLines, setLogLines]       = useState([]);
  const [streaming, setStreaming]     = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  // UI state
  const [confirm, setConfirm]         = useState(false);
  const [applying, setApplying]       = useState(false);
  const [checking, setChecking]       = useState(false);

  const logRef    = useRef(null);
  const closeSSE  = useRef(null);
  const pollTimer = useRef(null);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const scrollLog = useCallback(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, []);

  const loadVersionInfo = useCallback(async (force = false) => {
    if (!token) return;
    setVersionLoading(true);
    setVersionError(null);
    try {
      const fn = force ? forceCheckUpdate : getUpdateStatus;
      const data = await fn(token);
      setVersionInfo(data);
    } catch (err) {
      setVersionError(err.message);
    } finally {
      setVersionLoading(false);
    }
  }, [token]);

  // `refresh` re-reads each server's version.txt over SSH, which is a round trip per
  // host - so it is what a button does, not what a page load does.
  const loadFleet = useCallback(async (refresh = false) => {
    if (!token) return;
    setFleetError("");
    if (refresh) setFleetBusy(true);
    try {
      setFleet(await getFleetVersions(token, { refresh }));
    } catch (err) {
      setFleetError(err.message);
    } finally {
      setFleetBusy(false);
    }
  }, [token]);

  const loadComponentVersions = useCallback(async () => {
    if (!token) return;
    setCompLoading(true);
    try {
      const data = await getComponentVersions(token);
      setComponents(data.components ?? []);
    } catch {
      setComponents([]);
    } finally {
      setCompLoading(false);
    }
    loadFleet();
  }, [token, loadFleet]);

  const startFleetUpdate = useCallback(async () => {
    if (!token) return;
    setFleetBusy(true);
    setFleetError("");
    setFleetNote("");
    try {
      const res = await updateFleet(token);
      setFleetNote(t("update.fleet.started", { count: res.started?.length ?? 0 }));

      // Each host takes minutes; poll until none of them is still running, then
      // re-read the versions so the table shows what actually landed.
      const poll = setInterval(async () => {
        try {
          const st = await getFleetStatus(token);
          const running = (st.hosts ?? []).some((h) => h.state === "running");
          if (!running) {
            clearInterval(poll);
            setFleetBusy(false);
            loadFleet();
          }
        } catch {
          clearInterval(poll);
          setFleetBusy(false);
        }
      }, 5000);
    } catch (err) {
      setFleetError(err.message);
      setFleetBusy(false);
    }
  }, [token, t, loadFleet]);

  // Load version on mount + load last job status
  useEffect(() => {
    if (!token) return;
    loadVersionInfo();
    loadComponentVersions();
    getLastUpdateJob(token).then((job) => {
      if (job) { setJobId(job.jobId); setJobStatus(job); }
    }).catch(() => {});
  }, [token, loadVersionInfo, loadComponentVersions]);

  useEffect(() => { scrollLog(); }, [logLines, scrollLog]);

  // ── SSE + polling after disconnect ────────────────────────────────────────

  const startPolling = useCallback((id) => {
    if (pollTimer.current) return;
    setReconnecting(true);
    pollTimer.current = setInterval(async () => {
      try {
        const { job, lines } = await getUpdateProgress(token, id);
        if (job) {
          setJobStatus(job);
          setLogLines(lines || []);
          if (job.status === "complete" || job.status === "failed") {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
            setReconnecting(false);
            setStreaming(false);
            setApplying(false);
            loadVersionInfo(); // refresh version after update
          }
        }
      } catch {
        /* API may still be starting up — keep polling */
      }
    }, 3000);
  }, [token, loadVersionInfo]);

  const startStreaming = useCallback((id) => {
    setStreaming(true);
    setLogLines([]);
    setJobId(id);

    closeSSE.current = openUpdateStream(token, id, {
      onLine: (text) => {
        setLogLines((prev) => [...prev, text]);
      },
      onStatus: (phase) => {
        // Phase label update from updater without waiting for done event
        setJobStatus((prev) => prev ? { ...prev, status: phase } : prev);
      },
      onDone: (ok) => {
        setStreaming(false);
        setApplying(false);
        setJobStatus((prev) => ({ ...prev, status: ok ? "complete" : "failed", success: ok }));
        closeSSE.current = null;
        if (ok) loadVersionInfo();
      },
      onError: () => {
        // SSE dropped — API is restarting or idle-timed-out. Switch to polling.
        setApplying(false);
        closeSSE.current = null;
        startPolling(id);
      },
    });
  }, [token, loadVersionInfo, startPolling]);

  useEffect(() => () => {
    closeSSE.current?.();
    if (pollTimer.current) clearInterval(pollTimer.current);
  }, []);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleCheckNow = async () => {
    setChecking(true);
    await loadVersionInfo(true);
    setChecking(false);
  };

  const handleApplyClick = () => setConfirm(true);

  const handleDismiss = async () => {
    if (!jobId) { setJobStatus(null); return; }
    try {
      await dismissUpdateJob(token, jobId);
    } catch { /* best-effort */ }
    setJobStatus(null);
    setJobId(null);
    setLogLines([]);
    setApplying(false);
    clearInterval(pollTimer.current);
    pollTimer.current = null;
    setReconnecting(false);
    closeSSE.current?.();
    closeSSE.current = null;
  };

  const handleConfirm = async () => {
    setConfirm(false);
    setApplying(true);
    setLogLines([]);
    setJobStatus(null);
    try {
      const { jobId: id } = await applyUpdate(token, versionInfo?.latestVersion);
      setJobId(id);
      setJobStatus({ jobId: id, status: "starting", targetVersion: versionInfo?.latestVersion });
      startStreaming(id);
    } catch (err) {
      setApplying(false);
      setVersionError(err.message);
    }
  };

  // ── Derived display values ─────────────────────────────────────────────────

  const currentVer  = versionInfo?.currentVersion ?? t("update.unknown");
  const latestVer   = versionInfo?.latestVersion ?? "—";
  const hasUpdate   = versionInfo?.updateAvailable === true;
  const isRunning   = jobStatus && !["complete", "failed"].includes(jobStatus.status);
  const isDone      = jobStatus?.status === "complete";
  const isFailed    = jobStatus?.status === "failed";
  const phaseLabel  = jobStatus
    ? t(PHASE_KEYS[jobStatus.status] || "update.phase.starting", { defaultValue: jobStatus.status })
    : null;
  // A job is stale if it has been running for more than 3 minutes — show dismiss
  // regardless of the `applying` flag (the flag can stay true if SSE never fires done)
  const isStale     = isRunning && jobStatus?.startedAt &&
    (Date.now() - new Date(jobStatus.startedAt).getTime()) > 3 * 60 * 1000;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      {confirm && (
        <ConfirmModal
          version={versionInfo?.latestVersion}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(false)}
        />
      )}

      <div className="page-container adm">

        {/* ── Page header ── */}
        <Hero title={t("update.title")} subtitle={t("update.subtitle")} art={<UpdateArt />} />

        {/* ── Version Information ── */}
        <section className="adm-section">
          <div className="adm-section__head">
            <h2 className="adm-section__heading">{t("update.versionInfo")}</h2>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                className="adm-btn adm-btn--outline"
                onClick={handleCheckNow}
                disabled={checking || applying || isRunning}
              >
                <Icon name="refresh" size={14} />
                {checking ? t("update.checking") : t("update.checkNow")}
              </button>
              {hasUpdate && (
                <button
                  className="adm-btn adm-btn--primary"
                  onClick={handleApplyClick}
                  disabled={applying || (isRunning && !isStale)}
                >
                  <Icon name="download" size={14} />
                  {applying ? t("update.applyingUpdate") : t("update.applyUpdate", { version: latestVer })}
                </button>
              )}
            </div>
          </div>

          <div className="adm-overview-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>

            {/* Installed version */}
            <div className="adm-kpi-card">
              <div className="adm-kpi-card__top">
                <div>
                  <span className="adm-kpi-card__label">{t("update.currentVersion").toUpperCase()}</span>
                  <div className="adm-kpi-card__value" style={{ fontSize: "1.5rem" }}>
                    {versionLoading ? <span className="adm-kpi-card__skeleton" /> : currentVer}
                  </div>
                </div>
                <div className="adm-kpi-card__icon adm-kpi-card__icon--blue">
                  <Icon name="check" size={20} />
                </div>
              </div>
              <div className="adm-kpi-card__footer">
                <span className="adm-kpi-card__trend adm-kpi-card__trend--neutral">
                  {t("update.installedLabel")}
                </span>
              </div>
            </div>

            {/* Latest version */}
            <div className={`adm-kpi-card${hasUpdate ? " adm-kpi-card--warn" : ""}`}>
              <div className="adm-kpi-card__top">
                <div>
                  <span className="adm-kpi-card__label">{t("update.latestVersion").toUpperCase()}</span>
                  <div className="adm-kpi-card__value" style={{ fontSize: "1.5rem" }}>
                    {versionLoading ? <span className="adm-kpi-card__skeleton" /> : latestVer}
                  </div>
                </div>
                <div className={`adm-kpi-card__icon ${hasUpdate ? "adm-kpi-card__icon--amber" : "adm-kpi-card__icon--green"}`}>
                  <Icon name={hasUpdate ? "download" : "check"} size={20} />
                </div>
              </div>
              <div className="adm-kpi-card__footer">
                {hasUpdate ? (
                  <span className="adm-kpi-card__trend adm-kpi-card__trend--warn">
                    {t("update.updateAvailable")}
                  </span>
                ) : (
                  <span className="adm-kpi-card__trend adm-kpi-card__trend--up">
                    {t("update.upToDate")}
                  </span>
                )}
                {versionInfo?.lastChecked && (
                  <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                    {t("update.lastChecked")}: {new Date(versionInfo.lastChecked).toLocaleString()}
                  </span>
                )}
              </div>
            </div>

          </div>

          {versionError && (
            <div className="upd-alert upd-alert--error" style={{ marginTop: "14px" }}>
              <Icon name="info" size={16} />{versionError}
            </div>
          )}
          {versionInfo?.checkError && !versionError && (
            <div className="upd-alert upd-alert--warn" style={{ marginTop: "14px" }}>
              <Icon name="info" size={16} />{t("update.checkError")}: {versionInfo.checkError}
            </div>
          )}
        </section>

        {/* ── Other servers of this installation ── */}
        {/* Hidden on a single-machine installation: a section that only ever says
            "no other servers" is a section in the way. */}
        {fleet && (fleet.hosts?.length ?? 0) > 0 && (
          <section className="adm-section">
            <div className="adm-section__head">
              <h2 className="adm-section__heading">{t("update.fleet.title")}</h2>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="adm-btn adm-btn--outline"
                  onClick={() => loadFleet(true)}
                  disabled={fleetBusy}
                >
                  <Icon name="refresh" size={14} />
                  {t("update.fleet.refresh")}
                </button>
                <button
                  className="adm-btn adm-btn--primary"
                  onClick={startFleetUpdate}
                  disabled={fleetBusy || fleet.behind === 0}
                  title={fleet.behind === 0 ? t("update.fleet.allCurrent") : undefined}
                >
                  <Icon name="download" size={14} />
                  {t("update.fleet.updateAll")}
                </button>
              </div>
            </div>

            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px" }}>
              {t("update.fleet.intro", { version: fleet.thisServer?.version })}
            </p>

            {fleetError && (
              <div className="upd-alert upd-alert--error" style={{ marginBottom: 12 }}>{fleetError}</div>
            )}
            {fleetNote && (
              <div className="upd-alert upd-alert--warn" style={{ marginBottom: 12 }}>{fleetNote}</div>
            )}

            <div className="adm-cards-grid">
              {fleet.hosts.map((h) => {
                const known = !!h.installedVersion;
                const ok = h.upToDate;
                const accent = !known ? "#9ca3af" : ok ? "#22c55e" : "#f59e0b";
                return (
                  <div key={h.id} className="adm-section-card" style={{ cursor: "default", "--card-accent": accent }}>
                    <div
                      className="adm-section-card__icon-wrap"
                      style={{ background: ok ? "#f0fdf4" : known ? "#fffbeb" : "#f1f5f9", color: accent }}
                    >
                      <Icon name={!known ? "info" : ok ? "check" : "download"} size={20} />
                    </div>
                    <div className="adm-section-card__text">
                      <div className="adm-section-card__title">{h.displayName || h.hostName}</div>
                      <div
                        className="adm-section-card__subtitle"
                        style={{ fontFamily: "ui-monospace, monospace", color: known ? "#374151" : "#9ca3af", fontWeight: known ? 600 : 400 }}
                      >
                        {known
                          ? `${h.installedVersion}${ok ? "" : " · " + t("update.fleet.behind")}`
                          : t("update.fleet.unknown")}
                      </div>
                      {!h.updatable && (
                        <div className="adm-section-card__subtitle" style={{ color: "#9ca3af" }}>
                          {t("update.fleet.notUpdatable")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Installed Components ── */}
        <section className="adm-section">
          <div className="adm-section__head">
            <h2 className="adm-section__heading">{t("update.components.title")}</h2>
          </div>
          {/* Components can be updated one at a time now that a server may hold only
              some of them, so they can fall out of step with each other. Say so where
              it is visible, rather than leaving two numbers to be compared by eye. */}
          {(() => {
            const versions = [...new Set((components ?? [])
              .filter(c => c.installed !== false && c.version && c.version !== "unknown")
              .map(c => c.version))];
            if (versions.length < 2) return null;
            return (
              <div className="upd-alert upd-alert--warn" style={{ marginBottom: 12 }}>
                {t("update.components.mixedVersions", { versions: versions.join(", ") })}
              </div>
            );
          })()}
          <div className="adm-cards-grid">

            {/* UI — version baked in at build time */}
            <div className="adm-section-card" style={{ cursor: "default", "--card-accent": "#3b82f6" }}>
              <div className="adm-section-card__icon-wrap" style={{ background: "#eff6ff", color: "#3b82f6" }}>
                <Icon name="terminal" size={20} />
              </div>
              <div className="adm-section-card__text">
                <div className="adm-section-card__title">{t("update.components.ui")}</div>
                <div className="adm-section-card__subtitle" style={{ fontFamily: "ui-monospace, monospace", color: "#374151", fontWeight: 600 }}>
                  {process.env.REACT_APP_VERSION || pkgJson.version || t("update.components.versionUnknown")}
                </div>
              </div>
            </div>

            {/* Server components */}
            {compLoading && !components && [1,2,3,4,5].map(i => (
              <div key={i} className="adm-section-card" style={{ cursor: "default" }}>
                <div className="adm-kpi-card__skeleton" style={{ width: "100%", height: "24px" }} />
              </div>
            ))}
            {(components ?? []).map((c) => {
              // A server can be installed with only some components, so absence is a
              // normal state to show plainly - not the same thing as a version that
              // could not be read.
              const installed = c.installed !== false;
              const known = installed && c.version !== "unknown";
              return (
                <div key={c.key} className="adm-section-card" style={{ cursor: "default", "--card-accent": known ? "#22c55e" : "#9ca3af" }}>
                  <div className="adm-section-card__icon-wrap" style={{ background: known ? "#f0fdf4" : "#f1f5f9", color: known ? "#16a34a" : "#6b7280" }}>
                    <Icon name={installed ? "check" : "minus"} size={20} />
                  </div>
                  <div className="adm-section-card__text">
                    <div className="adm-section-card__title">{t(`update.components.${c.key}`, { defaultValue: c.name })}</div>
                    <div className="adm-section-card__subtitle" style={{ fontFamily: "ui-monospace, monospace", color: known ? "#374151" : "#9ca3af", fontWeight: known ? 600 : 400 }}>
                      {!installed
                        ? t("update.components.notInstalled")
                        : known ? c.version : t("update.components.versionUnknown")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Progress panel ── */}
        {(jobStatus || streaming) && (
          <section className="adm-section">
            <div className="adm-section__head">
              <h2 className="adm-section__heading">
                {t("update.progress")}
                {phaseLabel && (
                  <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: 400, opacity: 0.6, textTransform: "none", letterSpacing: 0 }}>
                    — {phaseLabel}
                  </span>
                )}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {isDone    && <span className="upd-badge upd-badge--success"><Icon name="check" size={13} />{t("update.updateComplete")}</span>}
                {isFailed  && <span className="upd-badge upd-badge--error"><Icon name="x" size={13} />{t("update.updateFailed")}</span>}
                {isRunning && <span className="upd-badge upd-badge--info"><span className="upd-spinner" />{phaseLabel || t("update.applyingUpdate")}</span>}
                {reconnecting && <span className="upd-badge upd-badge--warn"><Icon name="clock" size={13} />{t("update.reconnecting")}</span>}
                {jobStatus?.targetVersion && <span style={{ fontSize: "12px", color: "#6b7280" }}>→ v{jobStatus.targetVersion}</span>}
                {(isStale || isFailed) && (
                  <button className="adm-btn adm-btn--outline" style={{ fontSize: "12px", padding: "4px 10px" }} onClick={handleDismiss}>
                    {t("update.dismissStale", { defaultValue: "Dismiss & Retry" })}
                  </button>
                )}
              </div>
            </div>
            <div ref={logRef} className="upd-log" aria-label={t("update.progress")}>
              {logLines.length === 0 && (
                <span className="upd-log__empty">
                  {streaming ? t("update.waitingForOutput") : t("update.noLogs")}
                </span>
              )}
              {logLines.map((line, i) => (
                <div key={i} className="upd-log__line">{line}</div>
              ))}
            </div>
          </section>
        )}

        {/* ── Last update info ── */}
        {!jobStatus && !streaming && (
          <section className="adm-section">
            <div className="adm-section__head">
              <h2 className="adm-section__heading">{t("update.lastUpdate")}</h2>
            </div>
            <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>{t("update.neverUpdated")}</p>
          </section>
        )}
      </div>

      <style>{`
        .upd-overlay { position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:1000; }
        .upd-modal { background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:2rem;max-width:460px;width:90%;box-shadow:0 8px 32px rgba(0,0,0,.18); }
        .upd-modal__title { margin:0 0 .75rem;font-size:1.1rem;font-weight:700; }
        .upd-modal__body  { margin:0 0 1.5rem;line-height:1.6;color:#4b5563; }
        .upd-modal__actions { display:flex;gap:.75rem; }
        .upd-alert { display:flex;align-items:center;gap:.5rem;padding:.6rem .9rem;border-radius:8px;font-size:.875rem; }
        .upd-alert--error { background:#fef2f2;color:#b91c1c;border:1px solid #fca5a5; }
        .upd-alert--warn  { background:#fffbeb;color:#92400e;border:1px solid #fcd34d; }
        .upd-badge { display:inline-flex;align-items:center;gap:.35rem;padding:.25rem .65rem;border-radius:999px;font-size:.78rem;font-weight:600; }
        .upd-badge--success { background:#dcfce7;color:#166534; }
        .upd-badge--error   { background:#fee2e2;color:#991b1b; }
        .upd-badge--info    { background:#dbeafe;color:#1e40af; }
        .upd-badge--warn    { background:#fef9c3;color:#854d0e; }
        .upd-spinner { width:12px;height:12px;border-radius:50%;border:2px solid currentColor;border-top-color:transparent;animation:upd-spin .7s linear infinite;display:inline-block; }
        @keyframes upd-spin { to { transform:rotate(360deg); } }
        .upd-log { background:#0f172a;color:#94a3b8;border-radius:10px;padding:1rem 1.25rem;font-family:ui-monospace,monospace;font-size:.8rem;line-height:1.6;max-height:360px;overflow-y:auto;border:1px solid #1e293b; }
        .upd-log__line  { white-space:pre-wrap;word-break:break-all; }
        .upd-log__empty { opacity:.4;font-style:italic; }
      `}</style>
    </div>
  );
}
